<template>
  <div class="card">
    <div class="card-header">
      <h3 class="cart-title">
        <a data-action="collapse">Reminder</a>
      </h3>
      <div class="heading-elements">
        <ul class="list-inline mb-0">
          <li>
            <a data-action="collapse"><i data-feather="chevron-down" /></a>
          </li>
        </ul>
      </div>
    </div>
    <div class="card-content collapse show">
      <div class="card-body">
        <p class="text-muted">
          Set up automatic reminders for this OSH document
        </p>

        <!-- Enable Reminder -->
        <div class="row">
          <div class="col-12">
            <div class="mb-1">
              <div class="form-check form-check-success form-switch d-flex align-items-center">
                <input
                  id="enableReminder"
                  v-model="reminderData.enabled"
                  type="checkbox"
                  class="form-check-input"
                  @change="updateReminder"
                >
                <label
                  class="form-check-label"
                  for="enableReminder"
                ><small class="ms-1">Enable automatic reminders</small></label>
              </div>
            </div>
          </div>
        </div>

        <!-- Reminder Settings -->
        <div
          v-if="reminderData.enabled"
          class="ms-4"
        >
          <hr>

          <!-- Reminder Frequency -->
          <div class="row">
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="reminderFrequency"
                  class="form-label"
                >Reminder Frequency</label>
                <v-select
                  id="reminderFrequency"
                  v-model="reminderData.frequency"
                  :options="frequencyOptions"
                  :reduce="option => option.value"
                  label="label"
                  @input="updateReminder"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="reminderTarget"
                  class="form-label"
                >Send reminders to</label>
                <v-select
                  id="reminderTarget"
                  v-model="reminderData.target_audience"
                  :options="targetAudienceOptions"
                  :reduce="option => option.value"
                  label="label"
                  @input="updateReminder"
                />
              </div>
            </div>
          </div>

          <!-- Reminder Date -->
          <div class="row">
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="reminderDate"
                  class="form-label"
                >Start date</label>
                <input
                  id="reminderDate"
                  v-model="reminderData.start_date"
                  type="date"
                  class="form-control"
                  @change="updateReminder"
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="expirationDate"
                  class="form-label"
                >End date</label>
                <input
                  id="expirationDate"
                  v-model="reminderData.expiration_date"
                  type="date"
                  class="form-control"
                  @change="updateReminder"
                >
              </div>
            </div>
            <div class="col-12">
              <div class="mb-1">
                <label
                  for="daysBefore"
                  class="form-label"
                >Next reminder date</label>
                <div>
                  <strong>{{ getNextReminderDateDisplay() }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Send Via -->
          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <label class="form-label d-block">Send reminder via</label>
                <div class="d-flex gap-3">
                  <div class="form-check form-check-success form-switch d-flex align-items-center">
                    <input
                      id="sendViaIntranet"
                      v-model="reminderData.send_via_intranet"
                      type="checkbox"
                      class="form-check-input"
                      @change="updateReminder"
                    >
                    <label
                      class="form-check-label"
                      for="sendViaIntranet"
                    ><small class="ms-1">Intranet</small></label>
                  </div>
                  <div class="form-check form-check-success form-switch d-flex align-items-center">
                    <input
                      id="sendViaEmail"
                      v-model="reminderData.send_via_email"
                      type="checkbox"
                      class="form-check-input"
                      @change="updateReminder"
                    >
                    <label
                      class="form-check-label"
                      for="sendViaEmail"
                    ><small class="ms-1">Email</small></label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr>

          <!-- Reminder Message -->
          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <label
                  for="reminderTitle"
                  class="form-label"
                >Reminder Title</label>
                <input
                  id="reminderTitle"
                  v-model="reminderData.title"
                  type="text"
                  class="form-control"
                  placeholder="Enter reminder title"
                  @input="updateReminder"
                >
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <label
                for="reminderMessage"
                class="form-label"
              >Reminder Message</label>
              <div
                id="full-wrapper"
                class="mb-0"
              >
                <div id="full-container">
                  <quill-editor
                    v-model="reminderData.message"
                    class="h-100"
                    @input="updateReminder"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr>

          <!-- Additional Reminder Options -->
          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <div class="form-check form-check-success form-switch d-flex align-items-center">
                  <input
                    id="requireAcknowledgment"
                    v-model="reminderData.require_acknowledgment"
                    type="checkbox"
                    class="form-check-input"
                    @change="updateReminder"
                  >
                  <label
                    class="form-check-label"
                    for="requireAcknowledgment"
                  ><small class="ms-1">Require acknowledgment/confirmation</small></label>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <div class="form-check form-check-success form-switch d-flex align-items-center">
                  <input
                    id="stopAfterAcknowledgment"
                    v-model="reminderData.stop_after_acknowledgment"
                    type="checkbox"
                    class="form-check-input"
                    @change="updateReminder"
                  >
                  <label
                    class="form-check-label"
                    for="stopAfterAcknowledgment"
                  ><small class="ms-1">Stop reminders after user acknowledges</small></label>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="maxReminders"
                  class="form-label"
                >Maximum number of reminders</label>
                <input
                  id="maxReminders"
                  v-model="reminderData.max_reminders"
                  type="number"
                  class="form-control"
                  min="1"
                  max="10"
                  @input="updateReminder"
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-1">
                <label
                  for="reminderPriority"
                  class="form-label"
                >Reminder Priority</label>
                <v-select
                  id="reminderPriority"
                  v-model="reminderData.priority"
                  :options="priorityOptions"
                  :reduce="option => option.value"
                  label="label"
                  @input="updateReminder"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <div class="form-check form-check-success form-switch d-flex align-items-center">
                  <input
                    id="escalateReminder"
                    v-model="reminderData.escalate_to_admin"
                    type="checkbox"
                    class="form-check-input"
                    @change="updateReminder"
                  >
                  <label
                    class="form-check-label"
                    for="escalateReminder"
                  ><small class="ms-1">Escalate to admin after no response</small></label>
                </div>
              </div>
              <div
                v-if="reminderData.escalate_to_admin"
                class="mb-1 ms-4"
              >
                <label
                  for="escalateAfter"
                  class="form-label"
                >Escalate after how many reminders?</label>
                <input
                  id="escalateAfter"
                  v-model="reminderData.escalate_after_count"
                  type="number"
                  class="form-control"
                  min="1"
                  @input="updateReminder"
                >
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <div class="mb-1">
                <label
                  for="ccEmails"
                  class="form-label"
                >CC admin emails (optional)</label>
                <input
                  id="ccEmails"
                  v-model="reminderData.cc_emails"
                  type="text"
                  class="form-control"
                  placeholder="Enter email addresses separated by commas"
                  @input="updateReminder"
                >
                <small class="text-muted">Admin will receive a copy of all reminders</small>
              </div>
            </div>
          </div>

          <hr>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'

export default {
  name: 'Reminder',
  components: {
    vSelect,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      reminderData: {
        target_audience: 'all',
        enabled: false,
        frequency: 'monthly',
        days_before: 7,
        send_via_intranet: true,
        send_via_email: false,
        require_acknowledgment: true,
        stop_after_acknowledgment: true,
        max_reminders: 3,
        priority: 'normal',
        escalate_to_admin: false,
        escalate_after_count: 2,
        cc_emails: '',
        next_reminder_date: '',
        expiration_date: '',
        title: '',
        message: '',
        send_to_specific: false,
        specific_users: '',
        ...this.data,
      },
      frequencyOptions: [
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' },
        { label: 'Semi-Annually', value: 'semi-annually' },
        { label: 'Annually', value: 'annually' },
      ],
      targetAudienceOptions: [
        { label: 'All recipients', value: 'all' },
        { label: 'Only those who didn\'t answer', value: 'no_answer' },
      ],
      priorityOptions: [
        { label: 'Low', value: 'low' },
        { label: 'Normal', value: 'normal' },
        { label: 'High', value: 'high' },
        { label: 'Urgent', value: 'urgent' },
      ],
    }
  },
  watch: {
    data: {
      handler(newVal) {
        this.reminderData = {
          ...this.reminderData,
          ...newVal,
        }
      },
      deep: true,
    },
  },
  methods: {
    updateReminder() {
      const { start_date: startDate, next_reminder_date: nextReminderDate } = this.reminderData

      if (startDate) {
        if (!nextReminderDate || startDate > nextReminderDate) {
          this.reminderData.next_reminder_date = startDate
        }
      }
      this.$emit('saveReminder', this.reminderData)
    },
    getNextReminderDateDisplay() {
      const dateStr = this.reminderData.next_reminder_date || this.reminderData.start_date
      if (!dateStr) return 'N/A'

      const parts = String(dateStr).split('-').map(Number)
      if (parts.length !== 3 || parts.some(val => Number.isNaN(val))) return 'N/A'

      const [y, m, d] = parts
      return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`
    },
  },
}
</script>

<style scoped>
.editor {
  min-height: 200px;
}
</style>
